import type { Movimentacao, Solicitacao, StatusSolicitacao } from '../../../models';
import { calcularAlertaMovimentacao } from '../../../models';
import type { Pendencia } from '../../SolicitacaoDetailsPage/pendenciaMovimentacao';
import { pendenciaSolicitacao } from '../../SolicitacaoDetailsPage/pendenciaMovimentacao';
import { TIPO_FLUXO_LABELS } from '../../SolicitacaoDetailsPage/solicitacaoDetailsPage.definitions';
import { STATUS_SOLICITACAO_LABELS } from '../solicitacoesListPage.definitions';
import type { AgrupamentoKanban, ColunaCustomizada } from './solicitacoesKanban.types';

const COLUNAS_PRINCIPAIS: StatusSolicitacao[] = ['aberta', 'em_andamento', 'concluida'];
const SEM_PENDENCIA = 'Sem pendência';

export interface ColunaComputada {
  chave: string;
  label: string;
  /** Presente quando a coluna representa um status real — sempre no agrupamento "status";
   * no "customizado", só se essa coluna específica foi configurada como status (o usuário
   * pode misturar colunas de status com colunas de alerta/pendência no mesmo board). É o
   * que decide se dá pra arrastar card pra dentro dela (arrastar só faz sentido quando
   * soltar corresponde a uma transição de status de verdade). No "pendencia", nunca. */
  statusAlvo?: StatusSolicitacao;
}

export interface ItemComputado {
  solicitacao: Solicitacao;
  movimentacao: Movimentacao | null;
  alerta: ReturnType<typeof calcularAlertaMovimentacao> | null;
  pendencia: Pendencia | null;
  dataFormatada: string | null;
  clienteNome: string | null;
}

/** `ClientSummary` só precisa de id/name aqui — evita importar o tipo completo (que vem de
 * fora do módulo SI) só por causa de 2 campos. */
interface ClienteResumo {
  id: string;
  name: string;
}

export function computarItensKanban(
  itens: Solicitacao[],
  movimentacoesPorSolicitacao: Map<string, Movimentacao[]>,
  clientes: ClienteResumo[],
  mostrarPendencia: boolean
): ItemComputado[] {
  return itens.map((solicitacao) => {
    const movimentacoes = movimentacoesPorSolicitacao.get(solicitacao.id) ?? [];
    const movimentacao = movimentacaoRelevante(movimentacoes);
    const alerta = movimentacao ? calcularAlertaMovimentacao(movimentacao) : null;
    // Mesmo critério do resto da tela (lista/split-view): pendência só faz sentido pra
    // quem ainda dá pra agir (Em Andamento) — evita badge de pendência aparecer em
    // Solicitações "Novo" que nem começaram a ser tratadas.
    const pendencia =
      mostrarPendencia && solicitacao.status === 'em_andamento'
        ? pendenciaSolicitacao(solicitacao, movimentacoes)
        : null;
    return {
      solicitacao,
      movimentacao,
      alerta,
      pendencia,
      dataFormatada: dataPrevistaComFluxo(movimentacao),
      clienteNome: clientes.find((cliente) => cliente.id === solicitacao.clienteId)?.name ?? null,
    };
  });
}

/** Lista vazia em `statusSelecionados` = "sem filtro" (mostra tudo), mesma convenção do
 * resto da tela (ver SolicitacoesListPage.listaFiltrada). */
export function statusOcultoPeloFiltro(statusSelecionados: StatusSolicitacao[], status: StatusSolicitacao): boolean {
  return statusSelecionados.length > 0 && !statusSelecionados.includes(status);
}

/** `undefined` = mostra o "Nada aqui." padrão; senão, explica que o filtro de Status da
 * tela é quem está escondendo, não que a coluna não tem nenhuma solicitação mesmo. */
export function mensagemColunaVazia(
  coluna: ColunaComputada,
  itensDaColuna: ItemComputado[],
  statusSelecionados: StatusSolicitacao[]
): string | undefined {
  if (itensDaColuna.length > 0 || coluna.statusAlvo == null) return undefined;
  if (!statusOcultoPeloFiltro(statusSelecionados, coluna.statusAlvo)) return undefined;
  return `O filtro de Status está escondendo "${coluna.label}" — não significa que não existe nenhuma. Ajuste em Filtros pra ver.`;
}

export function movimentacaoRelevante(movimentacoes: Movimentacao[]): Movimentacao | null {
  if (movimentacoes.length === 0) return null;
  const ativa = movimentacoes.find(
    (m) => m.statusMovimentacao !== 'entregue' && m.statusMovimentacao !== 'cancelada'
  );
  return ativa ?? movimentacoes[movimentacoes.length - 1];
}

export function paraDataFormatada(timestamp: Movimentacao['dataPrevista']): string | null {
  if (!timestamp) return null;
  const data = 'toDate' in timestamp ? timestamp.toDate() : new Date(timestamp as unknown as string);
  return data.toLocaleDateString('pt-BR');
}

/** Data prevista com a Movimentação a que pertence ("Devolução · 12/11/2026") — nos cards do
 * Controle a data é a da Movimentação em andamento, que nem sempre é a 1ª. */
export function dataPrevistaComFluxo(movimentacao: Movimentacao | null | undefined): string | null {
  const data = movimentacao ? paraDataFormatada(movimentacao.dataPrevista) : null;
  return movimentacao && data ? `${TIPO_FLUXO_LABELS[movimentacao.tipoFluxo]} · ${data}` : null;
}

const CHAVE_AGRUPAMENTO = (uid: string) => `si:solicitacoesKanbanAgrupamento:${uid}`;
const CHAVE_COLUNAS_CUSTOM = (uid: string) => `si:solicitacoesKanbanColunasCustom:${uid}`;
/** Prefixo padrão = Kanban de Solicitações; outros boards (ex.: Estadias em Cliente)
 * passam o próprio, pra não misturar larguras de colunas com a mesma chave. */
export const PREFIXO_LARGURAS_SOLICITACOES = 'si:solicitacoesKanbanLargurasColuna';
const CHAVE_LARGURAS_COLUNA = (uid: string, prefixo: string) => `${prefixo}:${uid}`;

/** Largura arrastável por coluna (mesma ideia do modo "Dividida") — quantos cards cabem por
 * linha vira consequência automática da largura (CSS `grid-template-columns: repeat(auto-
 * fill, minmax(150px, 1fr))` no componente da coluna), não um número escolhido à parte.
 * Padrão cabe ~2 cards; MIN cabe 1; MAX cabe uns 5-6. */
export const LARGURA_COLUNA_PADRAO = 340;
export const LARGURA_COLUNA_MIN = 170;
export const LARGURA_COLUNA_MAX = 900;
/** Chave = `coluna.chave` (status/label/tipo:valor, depende do agrupamento) — cada coluna
 * guarda sua própria largura, já que o pedido foi exatamente "uma coluna bem larga, outra
 * bem estreita", não um valor único pra tudo. */
export type LargurasColunaMap = Record<string, number>;

/** Mesmo padrão pragmático de persistência já usado pro modo de visualização/largura do
 * split-view — silenciosamente cai no padrão se localStorage não estiver disponível ou
 * o valor salvo for inválido, nunca trava a tela por causa disso. */
export function lerAgrupamentoPersistido(uid: string | undefined): AgrupamentoKanban {
  if (!uid) return 'status';
  try {
    const valor = localStorage.getItem(CHAVE_AGRUPAMENTO(uid));
    if (valor === 'status' || valor === 'pendencia' || valor === 'customizado') return valor;
  } catch {
    // localStorage indisponível — só usa o padrão, não é crítico.
  }
  return 'status';
}

export function salvarAgrupamentoPersistido(uid: string | undefined, agrupamento: AgrupamentoKanban): void {
  if (!uid) return;
  try {
    localStorage.setItem(CHAVE_AGRUPAMENTO(uid), agrupamento);
  } catch {
    // não é crítico — só não persiste.
  }
}

export function lerColunasCustomPersistidas(uid: string | undefined): ColunaCustomizada[] {
  if (!uid) return [];
  try {
    const bruto = localStorage.getItem(CHAVE_COLUNAS_CUSTOM(uid));
    if (!bruto) return [];
    const parsed = JSON.parse(bruto);
    if (Array.isArray(parsed)) return parsed as ColunaCustomizada[];
  } catch {
    // valor corrompido/indisponível — cai pra lista vazia.
  }
  return [];
}

export function salvarColunasCustomPersistidas(uid: string | undefined, colunas: ColunaCustomizada[]): void {
  if (!uid) return;
  try {
    localStorage.setItem(CHAVE_COLUNAS_CUSTOM(uid), JSON.stringify(colunas));
  } catch {
    // não é crítico — só não persiste.
  }
}

export function lerLargurasColunaPersistidas(
  uid: string | undefined,
  prefixo: string = PREFIXO_LARGURAS_SOLICITACOES
): LargurasColunaMap {
  if (!uid) return {};
  try {
    const bruto = localStorage.getItem(CHAVE_LARGURAS_COLUNA(uid, prefixo));
    if (!bruto) return {};
    const parsed = JSON.parse(bruto);
    if (parsed && typeof parsed === 'object') return parsed as LargurasColunaMap;
  } catch {
    // valor corrompido/indisponível — cai pro padrão em cada coluna.
  }
  return {};
}

export function salvarLargurasColunaPersistidas(
  uid: string | undefined,
  mapa: LargurasColunaMap,
  prefixo: string = PREFIXO_LARGURAS_SOLICITACOES
): void {
  if (!uid) return;
  try {
    localStorage.setItem(CHAVE_LARGURAS_COLUNA(uid, prefixo), JSON.stringify(mapa));
  } catch {
    // não é crítico — só não persiste.
  }
}

/** Deriva as colunas (e quem entra em cada uma) pro modo de agrupamento atual — ver
 * comentário de SolicitacoesKanban pra semântica de cada modo. */
export function computarColunas(
  agrupamento: AgrupamentoKanban,
  itens: Solicitacao[],
  itensComputados: ItemComputado[],
  mostrarPendencia: boolean,
  colunasCustom: ColunaCustomizada[]
): { colunas: ColunaComputada[]; membros: Map<string, ItemComputado[]> } {
  if (agrupamento === 'status') {
    const colunasStatus: ColunaComputada[] = [
      ...COLUNAS_PRINCIPAIS,
      ...(Array.from(new Set(itens.map((s) => s.status))).filter(
        (s) => !COLUNAS_PRINCIPAIS.includes(s)
      ) as StatusSolicitacao[]),
    ].map((status) => ({ chave: status, label: STATUS_SOLICITACAO_LABELS[status], statusAlvo: status }));
    const membrosPorColuna = new Map<string, ItemComputado[]>();
    for (const item of itensComputados) {
      const lista = membrosPorColuna.get(item.solicitacao.status) ?? [];
      lista.push(item);
      membrosPorColuna.set(item.solicitacao.status, lista);
    }
    return { colunas: colunasStatus, membros: membrosPorColuna };
  }

  if (agrupamento === 'pendencia') {
    const elegiveis = mostrarPendencia ? itensComputados.filter((i) => i.solicitacao.status === 'em_andamento') : [];
    const membrosPorColuna = new Map<string, ItemComputado[]>();
    for (const item of elegiveis) {
      const chave = item.pendencia?.label ?? SEM_PENDENCIA;
      const lista = membrosPorColuna.get(chave) ?? [];
      lista.push(item);
      membrosPorColuna.set(chave, lista);
    }
    const colunasPendencia: ColunaComputada[] = Array.from(membrosPorColuna.keys())
      .sort((a, b) => (a === SEM_PENDENCIA ? 1 : b === SEM_PENDENCIA ? -1 : a.localeCompare(b)))
      .map((chave) => ({ chave, label: chave }));
    return { colunas: colunasPendencia, membros: membrosPorColuna };
  }

  // customizado
  const membrosPorColuna = new Map<string, ItemComputado[]>();
  for (const coluna of colunasCustom) membrosPorColuna.set(`${coluna.tipo}:${coluna.valor}`, []);
  for (const item of itensComputados) {
    for (const coluna of colunasCustom) {
      const bate =
        (coluna.tipo === 'status' && item.solicitacao.status === coluna.valor) ||
        (coluna.tipo === 'alerta' && item.alerta?.texto === coluna.valor) ||
        (coluna.tipo === 'pendencia' && item.pendencia?.label === coluna.valor);
      if (bate) membrosPorColuna.get(`${coluna.tipo}:${coluna.valor}`)!.push(item);
    }
  }
  const colunasCustomizadas: ColunaComputada[] = colunasCustom.map((c) => ({
    chave: `${c.tipo}:${c.valor}`,
    label: c.label,
    statusAlvo: c.tipo === 'status' ? (c.valor as StatusSolicitacao) : undefined,
  }));
  return { colunas: colunasCustomizadas, membros: membrosPorColuna };
}
